import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet, apiPatch, apiPost, apiRemove } from 'api-service';
import { apis } from 'api-service/apis';
import {
  adaptCategoryResource,
  adaptTagResource,
  isSystemTag,
} from './resourceCategory.adapter';

/** v4 tag API. `:tag` is a numeric id or a `SYSTEM_TAG` name. */
const TAGS_API = '/search/api/v4/tags';
const tagPath = (id) => `${TAGS_API}/${id}`;
const tagResourcesPath = (id) => `${tagPath(id)}/resources`;

const fetchTagsPage = (page, perPage) =>
  apiGet(TAGS_API, { page, per_page: perPage });

const tagListPerPage = (thunkAPI) =>
  thunkAPI.getState().settingsReducer?.filters?.public?.pageContentCountSlider
    ?.selectedValue;

const sortTagsByPin = (tags) =>
  [...tags].sort((left, right) => Number(right.is_pin) - Number(left.is_pin));

const readTagsPage = async (page, perPage) => {
  const response = await fetchTagsPage(page, perPage);
  const { data, meta } = response.data.result;
  return { data, total: meta.total, perPage: meta.per_page };
};

/**
 * Every custom tag. The category grid only asks for one page; the modal and
 * the edit page still need the full set, so they walk the remaining pages.
 */
const readEveryTag = async (perPage) => {
  const firstPage = await readTagsPage(1, perPage);
  const pageSize = firstPage.perPage || perPage;
  if (!pageSize) return sortTagsByPin(firstPage.data);
  const pageCount = Math.ceil(firstPage.total / pageSize);
  const restPages = await Promise.all(
    Array.from({ length: Math.max(pageCount - 1, 0) }, (_, index) =>
      readTagsPage(index + 2, perPage)
    )
  );
  return sortTagsByPin([
    ...firstPage.data,
    ...restPages.flatMap((page) => page.data),
  ]);
};

/**
 * One page of the category grid. Page size is the user's content-per-page
 * setting. Pinned tags on that page come first.
 */
export const fetchResourceCategories = createAsyncThunk(
  'resourceCategoryReducer/fetchResourceCategories',
  async ({ page = 1 } = {}, thunkAPI) => {
    try {
      const result = await readTagsPage(page, tagListPerPage(thunkAPI));
      return {
        data: sortTagsByPin(result.data),
        total: result.total,
        page,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/** Every custom tag, for the add-to-category select and the edit lookup. */
export const fetchAllResourceCategories = createAsyncThunk(
  'resourceCategoryReducer/fetchAllResourceCategories',
  async (_, thunkAPI) => {
    try {
      return await readEveryTag(tagListPerPage(thunkAPI));
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/**
 * Resources of one platform inside a tag, one page at a time, newest first.
 * Callers pass `platform` as the v4 platform (e.g. `instagramusers`).
 */
export const fetchResourceCategoryResources = createAsyncThunk(
  'resourceCategoryReducer/fetchResourceCategoryResources',
  async ({ id, platform, q, page, count }, thunkAPI) => {
    try {
      const response = await apiGet(tagResourcesPath(id), {
        platform,
        q,
        page,
        per_page: count,
      });
      const { data, meta } = response.data.result;
      return {
        data: data.map(adaptTagResource),
        total: meta.total,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/**
 * Creates a tag. When `items` is sent, the tag and its items are created
 * together, all-or-nothing.
 */
export const createResourceCategory = createAsyncThunk(
  'resourceCategoryReducer/createResourceCategory',
  async ({ name, description, items }, thunkAPI) => {
    try {
      const response = await apiPost(TAGS_API, { name, description, items });
      return response.data.result.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateResourceCategory = createAsyncThunk(
  'resourceCategoryReducer/updateResourceCategory',
  async ({ id, name, description }, thunkAPI) => {
    try {
      const response = await apiPatch(tagPath(id), { name, description });
      return response.data.result.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteResourceCategory = createAsyncThunk(
  'resourceCategoryReducer/deleteResourceCategory',
  async (id, thunkAPI) => {
    try {
      await apiRemove(tagPath(id));
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/**
 * Adds resource items to a tag. Resolves to `{ added, skipped, failed,
 * items_count }`. `frontPlatform` lets the slice bump that tab's count.
 */
export const addCategoryResources = createAsyncThunk(
  'resourceCategoryReducer/addCategoryResources',
  async ({ id, items, skipInvalid = false }, thunkAPI) => {
    try {
      const response = await apiPost(tagResourcesPath(id), {
        items,
        skip_invalid: skipInvalid,
      });
      return response.data.result.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/** Resolves to `{ removed, not_found, items_count }`. */
export const removeCategoryResources = createAsyncThunk(
  'resourceCategoryReducer/removeCategoryResources',
  async ({ id, items }, thunkAPI) => {
    try {
      const response = await apiRemove(tagResourcesPath(id), { items });
      return response.data.result.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/**
 * Adds the resources of an `.xlsx` file. Always sent with `skip_invalid`, so
 * sources that are not found come back in `failed` instead of a 422. The
 * request is multipart; axios drops the JSON content type for FormData so the
 * browser sets the boundary.
 */
export const importCategoryResources = createAsyncThunk(
  'resourceCategoryReducer/importCategoryResources',
  async ({ id, file }, thunkAPI) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('skip_invalid', '1');
      const response = await apiPost(
        `${tagResourcesPath(id)}/import`,
        formData,
        { Accept: 'application/json' }
      );
      return response.data.result.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/**
 * Searches sources of one platform. `apiKey` is a key of the resource search
 * endpoints declared in `api-service/apis.js` (e.g. `instagramUsers`).
 */
export const searchCategorySources = createAsyncThunk(
  'resourceCategoryReducer/searchCategorySources',
  async ({ apiKey, ...params }, thunkAPI) => {
    try {
      const { path, params: defaultParams } = apis(apiKey, {});
      const response = await apiGet(path, {
        ...defaultParams,
        time_duration: undefined,
        full_text: 1,
        ...params,
      });
      const { data = [], meta, total } = response.data.result;
      return {
        data: (data || []).map(adaptCategoryResource),
        total: meta?.total ?? total,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/**
 * Tag info of the add/edit page: the blacklist or VIP tag by name, or a
 * custom tag looked up by walking every tag page (v4 has no single-tag read
 * for those).
 * The walk costs one request per tag page and runs on open, retry and after
 * every import. Once the backend serves `GET /v4/tags/:id` for custom tags,
 * read it the same way as the system tags and drop `readEveryTag` here.
 * The sources of each tab are requested separately, only when opened.
 */
export const fetchCategoryManage = createAsyncThunk(
  'resourceCategoryReducer/fetchCategoryManage',
  async ({ id }, thunkAPI) => {
    try {
      if (isSystemTag(id)) {
        const response = await apiGet(tagPath(id));
        return response.data.result.data;
      }
      const list = await readEveryTag(tagListPerPage(thunkAPI));
      const info = list.find((category) => String(category.id) === String(id));
      return info || thunkAPI.rejectWithValue({ notFound: true });
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
